import Div from '@/shared/Div';
import Breadcrumb from '@/shared/crisp/Breadcrumb';
import React, { FC, MemoExoticComponent, useCallback, useMemo } from 'react';
import MainHeader from '@/components/Product/MainHeader';
import { NextPageContext } from 'next';
import { getSubCategory } from '@/actions/ecom/category';
import dlv from 'dlv';
import getSingleString from '@/utils/string';
import PageLayout from '@/components/Layouts/PageLayout';
import Container from '@/shared/Container';
import useLayoutRenderer from '@/hooks/useLayoutRenderer';
import TrendingProducts from '@/components/Home/TrendingProducts';
import ProductBannerExtraLarge from '@/shared/crisp/ProductBannerExtraLarge';
import ssrErrorHandler from '@/errors/ssrErrorHandler';
import { NOT_FOUND } from '@/constants/app';
import { getSearchResults } from '@/actions/ecom/search';
import { getCategoryURL, getSubCategoryURL } from '@/utils/urls';
import fetchAndMergeProductsWithPrice from '@/utils/fetchAndMergeProductsWithPrice';
import { ECOM_HOME_URL } from '@/configs/constants';
import is from '@/utils/is';
import usePLP, { PLP_SIZE } from '@/hooks/usePLP';
import Filters from '@/components/PLP/Filters';
import PLP from '@/components/PLP';
import Accordion from '@/shared/crisp/Accordion';
import Content from '@/components/Seo/Content';
import { CMS_COMPONENTS } from '@/constants/cms';
import NewLaunches from '@/components/Home/NewLaunches';
import DynamicPlaceholder from '@/shared/crisp/DynamicPlaceholder';

type SubCategoryProps = {
  page: any;
  seo: any;
  host: any;
  data: any;
  subCategory: {
    name: string;
    subCategoryId: string;
    description: string;
    image: any;
  };
  plp: {
    products: any[];
    filters: any;
    pageCount: number;
    resultCount: number;
  };
  category: {
    name: string;
    categoryId: string;
  };
};

const STATIC_MAP: { [key: string]: MemoExoticComponent<any> | FC<any> } = {
  [CMS_COMPONENTS.SHARED.TRENDING_PRODUCTS]: TrendingProducts,
  [CMS_COMPONENTS.PRODUCT_BANNERS.EXTRA_LARGE]: ProductBannerExtraLarge,
  [CMS_COMPONENTS.SHARED.PLP]: PLP,
  [CMS_COMPONENTS.SHARED.ACCORDION]: Accordion,
  [CMS_COMPONENTS.SHARED.HTML]: Content,
  [CMS_COMPONENTS.SHARED.NEW_LAUNCHES]: NewLaunches,
  [CMS_COMPONENTS.SHARED.DYNAMIC_PLACEHOLDER]: DynamicPlaceholder,
};

function SubCategory({ page, seo, plp, category, subCategory }: SubCategoryProps) {
  const dataSet = useMemo(() => {
    const { categoryId, name } = category || {};
    return [
      { id: 1, text: 'Home', link: ECOM_HOME_URL },
      {
        id: 2,
        text: name,
        link: getCategoryURL(categoryId),
      },
      {
        id: 3,
        text: dlv(subCategory, 'name', ''),
        link: getSubCategoryURL(categoryId, dlv(subCategory, 'subCategoryId', '')),
      },
    ];
  }, [category, subCategory]);

  const {
    plpList,
    open,
    isEmptyProductsList,
    closeModal,
    handleOpenModal,
    modalType,
    currentPage,
    applySortByFilter,
    viewMoreProduct,
    isLoading,
    onApplyFilter,
    onApplySortBy,
    onResetFilter,
  } = usePLP(plp, getSearchResults, null, { 'subCategories.id': [subCategory.subCategoryId] });
  const renderPLPHeader = useCallback(
    (PLPProps?: any) => {
      return (
        <Div className="bg-gradient-to-b from-[#E6EAF8] to-white border-b border-co-subtle pb-6">
          <Container>
            <Div className="pt-s3">
              <Breadcrumb dataSet={dataSet} className="py-s3 lg:block" />
            </Div>
            <MainHeader mainHeader={subCategory} />
            <Filters
              modalType={modalType}
              closeModal={closeModal}
              open={open}
              filters={plpList.filters}
              onApplyFilter={onApplyFilter}
              onResetFilter={onResetFilter}
              onApplySortBy={onApplySortBy}
              show={!isEmptyProductsList}
              handleOpenModal={handleOpenModal}
              applySortByFilter={applySortByFilter}
            />
          </Container>
        </Div>
      );
    },
    [
      dataSet,
      subCategory,
      modalType,
      closeModal,
      open,
      plpList.filters,
      onApplyFilter,
      onResetFilter,
      onApplySortBy,
      isEmptyProductsList,
      handleOpenModal,
      applySortByFilter,
    ],
  );

  const componentProps = useMemo(() => {
    return {
      [CMS_COMPONENTS.SHARED.PLP]: {
        isLoading,
        products: plpList.products,
        resultCount: plpList.resultCount,
        page: currentPage,
        viewMoreProduct,
        renderPLPHeader,
      },
    };
  }, [
    currentPage,
    isLoading,
    plpList.products,
    plpList.resultCount,
    renderPLPHeader,
    viewMoreProduct,
  ]);

  const layout = useLayoutRenderer(page, STATIC_MAP, componentProps);

  return <PageLayout seo={seo}>{layout}</PageLayout>;
}

export async function getServerSideProps(nextPageContext: NextPageContext) {
  try {
    const { query } = nextPageContext;
    const { slug } = query;
    const subCategoryId = getSingleString(slug?.[1]);
    if (!subCategoryId) return NOT_FOUND;
    const promises = [
      getSubCategory(subCategoryId),
      getSearchResults({
        q: null,
        filters: { 'subCategories.id': [subCategoryId] },
        page: 1,
        size: PLP_SIZE,
      }),
    ];
    const [page, searchResults] = await Promise.all(promises);
    const { products, filters, pageCount = 1, resultCount = 0 } = searchResults?.data;
    const { name, description, icon, layout, seo, category } = page;

    const isPLPInLayout = Boolean(
      layout.find((item: any) => item.__component === CMS_COMPONENTS.SHARED.PLP),
    );

    let productsWithPrice: any = [];
    if (isPLPInLayout && is.not.empty.array(products)) {
      productsWithPrice = (await fetchAndMergeProductsWithPrice(products || [])) ?? null;
    }

    const subCategory = {
      name,
      subCategoryId,
      image: dlv(icon, 'data.attributes.formats.thumbnail', dlv(icon, 'data.attributes', {})),
      description,
    };

    return {
      props: {
        page: layout,
        seo,
        plp: { products: productsWithPrice, filters, pageCount, resultCount },
        category: {
          name: dlv(category, 'data.attributes.name', {}),
          categoryId: dlv(category, 'data.attributes.categoryId', {}),
        },
        subCategory,
      },
    };
  } catch (error) {
    return ssrErrorHandler(error, nextPageContext);
  }
}

export default React.memo(SubCategory);
